"use client";

import { c as _c } from "react/compiler-runtime";
import { CircularProgress, InputAdornment, TextField, type TextFieldProps } from "@mui/material";
import React, { useEffect, useState } from "react";
type DebouncedInputProps = Omit<TextFieldProps, "onChange"> & {
  value: string;
  onChange: (value: string) => void;
  timeout?: number;
};
export const DebouncedInput: React.FC<DebouncedInputProps> = t0 => {
  const $ = _c(35);
  let inputRef;
  let onChange;
  let restProps;
  let slotProps;
  let timeout;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      onChange,
      timeout,
      slotProps,
      inputRef,
      ...restProps
    } = t0);
    $[0] = t0;
    $[1] = inputRef;
    $[2] = onChange;
    $[3] = restProps;
    $[4] = slotProps;
    $[5] = timeout;
    $[6] = value;
  } else {
    inputRef = $[1];
    onChange = $[2];
    restProps = $[3];
    slotProps = $[4];
    timeout = $[5];
    value = $[6];
  }
  const [rawInput, setRawInput] = useState(value);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [syncedExternalValue, setSyncedExternalValue] = useState(value);
  if (value !== syncedExternalValue) {
    setSyncedExternalValue(value);
    if (!hasPendingChanges) {
      setRawInput(value);
    }
  }
  let t1;
  if ($[7] !== onChange || $[8] !== rawInput || $[9] !== timeout || $[10] !== value) {
    t1 = () => {
      const timeoutId = setTimeout(() => {
        setHasPendingChanges(false);
        if (rawInput === value) {
          return;
        }
        onChange(rawInput);
      }, timeout ?? 500);
      return () => {
        clearTimeout(timeoutId);
      };
    };
    $[7] = onChange;
    $[8] = rawInput;
    $[9] = timeout;
    $[10] = value;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== onChange || $[13] !== rawInput || $[14] !== restProps.error || $[15] !== timeout || $[16] !== value) {
    t2 = [onChange, rawInput, restProps.error, timeout, value];
    $[12] = onChange;
    $[13] = rawInput;
    $[14] = restProps.error;
    $[15] = timeout;
    $[16] = value;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  useEffect(t1, t2);
  let t3;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ev => {
      setRawInput(ev.target.value);
      setHasPendingChanges(true);
    };
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  const handleChange = t3;
  const inputSlotProps = slotProps?.input;
  const endAdornment = typeof inputSlotProps === "object" && inputSlotProps !== null && "endAdornment" in inputSlotProps ? inputSlotProps.endAdornment : null;
  let t4;
  if ($[19] !== inputSlotProps) {
    t4 = typeof inputSlotProps === "object" && inputSlotProps !== null ? inputSlotProps : {};
    $[19] = inputSlotProps;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== endAdornment || $[22] !== hasPendingChanges) {
    t5 = hasPendingChanges ? <InputAdornment position="end"><CircularProgress size={24} /></InputAdornment> : endAdornment;
    $[21] = endAdornment;
    $[22] = hasPendingChanges;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== t4 || $[25] !== t5) {
    t6 = {
      ...t4,
      endAdornment: t5
    };
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== slotProps || $[28] !== t6) {
    t7 = {
      ...slotProps,
      input: t6
    };
    $[27] = slotProps;
    $[28] = t6;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== inputRef || $[31] !== rawInput || $[32] !== restProps || $[33] !== t7) {
    t8 = <TextField value={rawInput} onChange={handleChange} inputRef={inputRef} slotProps={t7} {...restProps} />;
    $[30] = inputRef;
    $[31] = rawInput;
    $[32] = restProps;
    $[33] = t7;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  return t8;
};